import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextField from 'modules/core/components/TextField'
import { employeeHasFrenchLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import ToggleSection from 'modules/employees/components/Personal/NewPersonal/ToggleSection/index'
import { useValidateEmailForCreation } from 'modules/employees/hooks/useValidateEmailForCreation'
import type Employee from 'modules/employees/store/models/Employee'
import { useCanEditEmail } from 'modules/profile/hooks/personal_info/useCanEditEmail'

import { ContactValues } from '../ContactValues'

interface Props {
  values: ContactValues
  onChangeValue: (fieldName: string, fieldValue: string | undefined) => void
  readonly: boolean
  employee: Employee
}

const ContactSection: React.FC<Props> = ({ values, onChangeValue, readonly, employee }) => {
  const emailValidation = useValidateEmailForCreation(values.personalEmail?.trim() || '')
  const hasCoffreFort = hasFeature('coffre_fort_activable')
  const { canEditEmail } = useCanEditEmail({ accessId: employee.attributes.access_id })
  const emailReadonlyTooltip = i18n.t('employee_form.email_readonly_tooltip')
  const emailSecundaryLabel = canEditEmail ? null : (
    <InformationTooltip information={emailReadonlyTooltip} />
  )

  return (
    <Box gap="s24">
      <Box
        paddingBottom="s8"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
        flexDirection="row"
        gap="s4"
      >
        <Text.Header2 color="grey1000" weight="medium">
          {i18n.t('employee_form.contact_section.title')}
        </Text.Header2>
        <Text.Header2 color="grey600" weight="medium">
          ({i18n.t('common.optional')})
        </Text.Header2>
      </Box>
      <Box flexDirection="row" gap="s24">
        <Box style={{ minWidth: '50%' }}>
          <TextField
            label={i18n.t('employee_form.contact_number')}
            value={values.phoneNumber}
            readonly={readonly}
            onChange={(value) => onChangeValue('phoneNumber', value)}
          />
        </Box>
        {hasCoffreFort &&
          employeeHasFrenchLegalEntity(employee) &&
          employee.policy.can('shareProfileEmail') && (
            <TextField
              type="email"
              readonly={!canEditEmail}
              secondaryLabel={emailSecundaryLabel}
              label={i18n.t('employee_form.personal_email.label')}
              value={values.personalEmail}
              error={emailValidation.errorString}
              onChange={(value) => onChangeValue('personalEmail', value)}
            />
          )}
      </Box>
      <ToggleSection
        description={i18n.t('employee_form.contact_section.emergency_contact')}
        openValue={!!values.contactNumber || !!values.contactName}
      >
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('employee_form.contact_section.emergy_contact_name')}
            value={values.contactName}
            readonly={readonly}
            onChange={(value) => onChangeValue('contactName', value)}
          />
          <TextField
            label={i18n.t('employee_form.contact_number')}
            value={values.contactNumber}
            readonly={readonly}
            onChange={(value) => onChangeValue('contactNumber', value)}
          />
        </Box>
      </ToggleSection>
    </Box>
  )
}

export default ContactSection
